import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { Report } from '../../types/reports';

// Function to parse response and add links to entity references
const parseResponseWithLinks = (response: string, contacts: any[]) => {
  if (!response) return response;
  
  let parsedResponse = response;
  
  // Find contact names in the response and replace with links
  contacts.forEach(contact => {
    const regex = new RegExp(`\\b${contact.name}\\b`, 'gi');
    parsedResponse = parsedResponse.replace(regex, (match) => {
      return `<a href="/contacts/${contact.id}" class="entity-link">${match}</a>`;
    });
  });
  
  // Convert markdown-style formatting to HTML
  parsedResponse = parsedResponse
    // Convert markdown links [text](url) to HTML links FIRST
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="entity-link">$1</a>')
    // Convert headers FIRST (before paragraph wrapping)
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/^# (.*)$/gm, '<h1>$1</h1>')
    // Convert horizontal rules
    .replace(/^---$/gm, '<hr>')
    // Convert numbered lists
    .replace(/^(\d+\.\s.*)$/gm, '<li>$1</li>')
    // Convert bullet points
    .replace(/^[-*]\s(.*)$/gm, '<li>$1</li>')
    // Wrap consecutive list items in ul/ol
    .replace(/(<li>.*<\/li>(\s*<li>.*<\/li>)*)/g, '<ul>$1</ul>')
    // Convert **bold** to <strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Convert *italic* to <em>
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Convert line breaks to <br>
    .replace(/\n\n/g, '</p><p>')
    // Wrap in paragraphs (but not headers, lists, or horizontal rules)
    .replace(/^(?!<[h1-6]|<li|<ul|<ol|<hr)(.*)$/gm, '<p>$1</p>')
    // Clean up empty paragraphs
    .replace(/<p><\/p>/g, '');
  
  return parsedResponse;
};

interface ReportModalProps {
  open: boolean;
  onClose: () => void;
  report: Report | null;
  onDelete?: (reportId: string) => void;
}

export function ReportModal({ open, onClose, report, onDelete }: ReportModalProps) {
  const supabase = useSupabaseClient();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!report) return null;

  const handleDelete = async () => {
    if (!report.id) return;
    
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      setDeleteError(null);

      const { error } = await supabase
        .from('reports')
        .delete()
        .eq('id', report.id);

      if (error) {
        throw error;
      }

      // Call the onDelete callback to refresh the parent component
      onDelete?.(report.id);
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error deleting report:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete report');
    } finally {
      setDeleting(false);
    }
  };

  const getReportTypeColor = (type: string) => {
    switch (type) {
      case 'ask_quant':
        return 'primary';
      case 'analysis':
        return 'secondary';
      case 'summary':
        return 'success';
      default:
        return 'default';
    }
  };

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case 'ask_quant':
        return 'Ask Quant Analysis';
      case 'analysis':
        return 'Data Analysis';
      case 'summary':
        return 'Summary Report';
      default:
        return type;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {report.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label={getReportTypeLabel(report.report_type)}
                size="small"
                color={getReportTypeColor(report.report_type) as any}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {new Date(report.created_at).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Button
          onClick={onClose}
          size="small"
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 3,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '4px',
        },
      }}>
        {/* Delete Error Alert */}
        {deleteError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {deleteError}
          </Alert>
        )}
        {/* Question if available */}
        {report.metadata?.question && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              mb: 3,
              backgroundColor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Question:
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              {report.metadata.question}
            </Typography>
          </Paper>
        )}

        {/* Search Results Summary */}
        {report.metadata?.search_results && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Data Sources:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip
                label={`${report.metadata.search_results.newsArticles} News Articles`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${report.metadata.search_results.exaResults} Web Results`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        )}

        {/* Report Content */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Analysis:
          </Typography>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box 
              sx={{ 
                lineHeight: 1.8,
                color: 'text.primary',
                fontSize: '0.95rem',
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontWeight: 600,
                  marginTop: 2,
                  marginBottom: 1,
                  color: 'text.primary',
                },
                '& h1': { fontSize: '1.5rem' },
                '& h2': { fontSize: '1.3rem' },
                '& h3': { fontSize: '1.1rem' },
                '& h4': { fontSize: '1rem' },
                '& p': {
                  marginBottom: 1.5,
                  lineHeight: 1.7,
                },
                '& ul, & ol': {
                  marginBottom: 1.5,
                  paddingLeft: 2,
                },
                '& li': {
                  marginBottom: 0.5,
                  lineHeight: 1.6,
                },
                '& strong': {
                  fontWeight: 600,
                  color: 'text.primary',
                },
                '& em': {
                  fontStyle: 'italic',
                  color: 'text.secondary',
                },
                '& hr': {
                  border: 'none',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  margin: '2rem 0',
                },
                '& a, & .entity-link': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 500,
                  padding: '0.1rem 0.2rem',
                  borderRadius: '0.25rem',
                  transition: 'all 0.2s ease',
                  display: 'inline-block',
                  '&:hover': {
                    color: 'primary.dark',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    textDecoration: 'none',
                    transform: 'translateY(-1px)',
                  }
                },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  paddingLeft: 2,
                  marginLeft: 0,
                  marginRight: 0,
                  marginTop: 1,
                  marginBottom: 1.5,
                  fontStyle: 'italic',
                  backgroundColor: 'action.hover',
                  padding: 1.5,
                  borderRadius: '0.5rem',
                }
              }}
              dangerouslySetInnerHTML={{ 
                __html: parseResponseWithLinks(report.content, []) 
              }}
            />
          </Paper>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Button 
          onClick={handleDelete} 
          variant="outlined" 
          color="error"
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete Report'}
        </Button>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
